import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Video, Rotate3d, Headset, Globe, Heart, Users, Landmark, Sprout, ArrowRight, ChevronDown, Music2 } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, MaskedLine, SectionHead } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import VillageCard from "@/components/site/VillageCard";
import MapMonferrato from "@/components/site/MapMonferrato";

const CONCEPT_ICONS = [Video, Headset, Globe, Heart];
const PILLAR_ICONS = [Video, Rotate3d, Headset];
const VISION_ICONS = [Users, Landmark, Sprout];

const HomePage = () => {
  const { t } = useLang();
  const { data: villages } = useApi("/villages");
  useSeo("home");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  const concepts = t("home.territorio.concepts");
  const pillars = t("home.esperienze.pillars");
  const visionPillars = t("home.visione.pillars");
  const highlights = t("home.visione.highlights");

  return (
    <div data-testid="home-page">
      {/* ————— HERO ————— */}
      <section ref={heroRef} className="relative h-screen min-h-[620px] overflow-hidden" data-testid="home-hero">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src="/assets/home-hero.webp"
            alt={t("home.heroAlt")}
            fetchpriority="high"
            className="hero-zoom w-full h-[115%] object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent" />

        <motion.div style={{ opacity: heroOpacity }} className="relative h-full max-w-[1440px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-28">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="flex items-center gap-4 mb-7"
          >
            <span className="h-px w-14 bg-[#C5A059]" />
            <span className="text-[11px] tracking-[0.35em] uppercase font-medium text-[#C5A059]">{t("home.eyebrow")}</span>
          </motion.div>

          <h1 className="font-serif-display font-normal text-[#F7F5F0] tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.45} immediate>{t("home.heroLine1")}</MaskedLine>
            <MaskedLine delay={0.58} immediate>{t("home.heroLine2")}</MaskedLine>
            <MaskedLine delay={0.71} immediate><span className="italic text-[#D8B46C]">{t("home.heroLine3")}</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#F7F5F0]/75"
          >
            {t("home.heroSub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/#esplora"
              data-testid="hero-explore-button"
              className="inline-flex items-center gap-3 bg-[#C5A059] text-[#121212] px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#D8B46C] transition-colors duration-500"
            >
              {t("home.ctaExplore")} <ArrowRight size={13} />
            </Link>
            <Link
              to="/esperienze"
              data-testid="hero-experience-button"
              className="inline-flex items-center gap-3 border border-[#F7F5F0]/40 text-[#F7F5F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#D8B46C] transition-colors duration-500"
            >
              {t("home.ctaExperience")}
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 text-[#F7F5F0]/60"
        >
          <span className="vertical-label text-[10px] tracking-[0.35em] uppercase">{t("common.scorri")}</span>
          <ChevronDown size={16} className="animate-bounce text-[#C5A059]" />
        </motion.div>
      </section>

      <Marquee />

      {/* ————— 01 · UN TERRITORIO DA VIVERE ————— */}
      <section id="territorio" className="py-24 lg:py-36" data-testid="territorio-da-vivere-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHead
              chapter={t("home.territorio.chapter")}
              eyebrow={t("home.territorio.eyebrow")}
              title={<>{t("home.territorio.titleA")}<br />{t("home.territorio.titleB")}</>}
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#666157]">
                {t("home.territorio.body")}
              </p>
            </Reveal>
            <div className="mt-12">
              {concepts.map((c, i) => {
                const Icon = CONCEPT_ICONS[i];
                return (
                  <Reveal key={c.t} delay={0.08 * i}>
                    <div className="flex items-start gap-5 py-5 border-t border-[#1C1B18]/10 group" data-testid={`concept-${i}`}>
                      <Icon size={20} strokeWidth={1.4} className="text-[#9E7B36] mt-1 shrink-0 transition-transform duration-500 group-hover:-translate-y-0.5" />
                      <div>
                        <h3 className="text-[12px] tracking-[0.22em] uppercase font-semibold text-[#1C1B18]">{c.t}</h3>
                        <p className="text-sm text-[#666157] mt-1">{c.d}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
              <div className="border-t border-[#1C1B18]/10" />
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <Reveal delay={0.2} className="h-full">
              <div className="relative h-full min-h-[420px]">
                <div className="absolute -top-4 -right-4 w-full h-full border border-[#C5A059]/40 pointer-events-none hidden lg:block" />
                <img
                  src="/assets/editorial-sunset.webp"
                  alt={t("home.territorio.imgAlt")}
                  loading="lazy"
                  className="w-full h-full object-cover photo-frame"
                />
                <div className="absolute bottom-0 left-0 bg-[#121212] text-[#F7F5F0] px-6 py-4">
                  <p className="font-serif-display italic text-lg">{t("home.territorio.formula")}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— 02 · ESPLORA ————— */}
      <section id="esplora" className="py-24 lg:py-32 bg-[#EFECE6]" data-testid="esplora-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <SectionHead chapter={t("home.esplora.chapter")} eyebrow={t("home.esplora.eyebrow")} title={t("home.esplora.title")} />
            <Reveal delay={0.2}>
              <p className="font-serif-display italic text-xl text-[#666157] lg:text-right">{t("home.esplora.subtitle")}</p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="villages-destination-grid">
            {(villages || []).map((v, i) => (
              <Reveal key={v.slug} delay={0.06 * i} y={40}>
                <VillageCard village={v} index={i} />
              </Reveal>
            ))}
          </div>
          {!villages && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-[#1C1B18]/5 animate-pulse" style={{ aspectRatio: "3/4" }} />)}
            </div>
          )}
        </div>
      </section>

      {/* ————— 03 · VIVI L'ESPERIENZA ————— */}
      <section id="esperienze" className="py-24 lg:py-36 bg-[#121212] text-[#F7F5F0] relative overflow-hidden" data-testid="immersive-experience-chamber">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead
            dark
            chapter={t("home.esperienze.chapter")}
            eyebrow={t("home.esperienze.eyebrow")}
            title={<>{t("home.esperienze.titleA")}<br /><span className="italic text-[#D8B46C]">{t("home.esperienze.titleB")}</span></>}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14 mt-16 lg:mt-20">
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <Reveal key={p.num} delay={0.12 * i}>
                  <div className="border-t border-[#C5A059]/30 pt-8 group" data-testid={`pillar-${p.num}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-serif-display italic text-5xl text-[#C5A059]/40 group-hover:text-[#C5A059] transition-colors duration-700">{p.num}</span>
                      <Icon size={24} strokeWidth={1.2} className="text-[#C5A059]" />
                    </div>
                    <h3 className="text-[13px] tracking-[0.25em] uppercase font-semibold mt-6">{p.t}</h3>
                    <p className="text-sm text-[#F7F5F0]/60 leading-relaxed mt-3">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.3}>
            <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Link
                to="/esperienze"
                data-testid="experiences-cta-button"
                className="inline-flex items-center gap-3 border border-[#C5A059]/60 px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A059] hover:text-[#121212] transition-colors duration-500"
              >
                {t("home.esperienze.cta")} <ArrowRight size={13} />
              </Link>
              <p className="text-xs text-[#F7F5F0]/40 max-w-sm">{t("home.esperienze.questNote")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— 04 · MAPPA ————— */}
      <section id="mappa" className="py-24 lg:py-32" data-testid="map-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <SectionHead chapter={t("home.mappa.chapter")} eyebrow={t("home.mappa.eyebrow")} title={t("home.mappa.title")} />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#666157] leading-relaxed">
                {t("home.mappa.body")}
              </p>
            </Reveal>
          </div>
          {villages && <MapMonferrato villages={villages} />}
        </div>
      </section>

      {/* ————— 05 · IL SUONO DEL MONFERRATO ————— */}
      <section className="py-24 lg:py-36 bg-[#0E0D0C] text-[#F7F5F0] overflow-hidden" data-testid="echoes-sound-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <Reveal>
              <div className="relative">
                <img
                  src="/assets/artwork-living-memories.webp"
                  alt={t("home.suono.imgAlt")}
                  loading="lazy"
                  className="w-full object-cover photo-frame"
                  style={{ aspectRatio: "1/1" }}
                  data-testid="home-echoes-artwork"
                />
                <div className="absolute -bottom-5 -right-5 w-24 h-24 border border-[#C5A059]/50 pointer-events-none" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 order-1 lg:order-2">
            <SectionHead
              dark
              chapter={t("home.suono.chapter")}
              eyebrow={t("home.suono.eyebrow")}
              title={<>{t("home.suono.titleA")}<br /><span className="italic text-[#D8B46C]">{t("home.suono.titleB")}</span></>}
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#F7F5F0]/70 max-w-2xl">
                {t("home.suono.body")}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="font-serif-display italic text-2xl text-[#C5A059] mt-8">{t("home.suono.highlight")}</p>
            </Reveal>
            <Reveal delay={0.35}>
              <Link
                to="/echoes-from-above"
                data-testid="echoes-cta-button"
                className="mt-10 inline-flex items-center gap-3 border border-[#C5A059]/60 px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A059] hover:text-[#121212] transition-colors duration-500"
              >
                <Music2 size={14} /> {t("home.suono.cta")}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— 06 · LA NOSTRA VISIONE ————— */}
      <section className="py-24 lg:py-32" data-testid="vision-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <SectionHead chapter={t("home.visione.chapter")} eyebrow={t("home.visione.eyebrow")} title={t("home.visione.title")} />
              <Reveal delay={0.15}>
                <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#666157]">
                  {t("home.visione.body")}
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="font-serif-display italic text-xl lg:text-2xl text-[#9E7B36] mt-8 leading-snug">
                  {highlights.map((h) => <span key={h} className="block">{h}</span>)}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {visionPillars.map((v, i) => {
                const Icon = VISION_ICONS[i];
                return (
                  <Reveal key={v.t} delay={0.1 * i}>
                    <div className="border-l border-[#C5A059]/40 pl-6 py-2 h-full" data-testid={`vision-${i}`}>
                      <Icon size={22} strokeWidth={1.4} className="text-[#9E7B36]" />
                      <h3 className="text-[12px] tracking-[0.22em] uppercase font-semibold mt-5">{v.t}</h3>
                      <p className="text-sm text-[#666157] mt-3 leading-relaxed">{v.d}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ————— FINAL CTA ————— */}
      <section className="relative h-[80vh] min-h-[520px] overflow-hidden flex items-center justify-center" data-testid="final-cta-section">
        <img src="/assets/home-hero.webp" alt={t("home.final.imgAlt")} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6">
          <Reveal>
            <span className="gold-rule-center block w-32 mx-auto mb-8" />
          </Reveal>
          <Reveal>
            <h2 className="font-serif-display text-[#F7F5F0] text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              {t("home.final.titleA")}<br /><span className="italic text-[#D8B46C]">{t("home.final.titleB")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 text-[#F7F5F0]/75 text-base sm:text-lg tracking-wide">{t("home.final.sub")}</p>
          </Reveal>
          <Reveal delay={0.45}>
            <Link
              to="/#esplora"
              data-testid="final-cta-explore-button"
              className="mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-[#121212] px-9 py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#D8B46C] transition-colors duration-500"
            >
              {t("home.final.cta")} <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
